import axios from 'axios';
import Cookies from 'js-cookie';
import store from "@/store";
import {snapshot} from "valtio";

// Track 401 errors to prevent infinite loop
let auth401Count = 0;
const MAX_401_RETRIES = 2;
let lastAuthReset = Date.now();

type ApiVersion = 'v1' | 'v2'

// Most services are on v2. Pass the version explicitly for the ones that are
// not — e.g. the search service only exposes v1 routes.
const createAxiosInstance = (baseURL: string, apiVersion: ApiVersion = 'v2') => {


    const token = Cookies.get('neatolabsToken');

    if (!token) {
        store.currentUser = null;
    }

    const instance = axios.create({
        baseURL,
        headers: {
            'Content-Type': 'application/json; charset=utf-8',
        },
    });

    instance.interceptors.request.use(config => {

        const {currentUser} = snapshot(store)
        let currentCompanyId = currentUser?.selectedCompany?.id
        const token = Cookies.get('neatolabsToken');

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (currentCompanyId) {
            config.baseURL = `${baseURL}/${apiVersion}/companies/${currentCompanyId}`;
        }

        if (!token) {
            store.currentUser = null;
            store.status.authExpired = true;
            return Promise.reject('No token');
        }

        return config;
    });




    instance.interceptors.response.use(
        response => {
            // Reset 401 counter on successful response
            auth401Count = 0;

            // Unwrap v2 envelope: { data: ... } → ...
            // Backend handlers using golib's WriteSuccessResponse wrap the payload in
            // a single-key { data: ... } object. Strip it so callers see the same
            // shape they would from a v1 (flat) response. Only unwraps when the body
            // is a plain object with exactly one key named "data" — arrays, primitives,
            // and multi-key bodies are left alone.
            const body = response.data
            if (
                body &&
                typeof body === 'object' &&
                !Array.isArray(body) &&
                Object.keys(body).length === 1 &&
                Object.prototype.hasOwnProperty.call(body, 'data')
            ) {
                response.data = (body as { data: unknown }).data
            }

            return response;
        },
        error => {
            if (error.response && error.response.status === 401 && error.response.data.message === "JWT is invalid.") {
                // Reset counter if more than 30 seconds have passed
                if (Date.now() - lastAuthReset > 30000) {
                    auth401Count = 0;
                }

                auth401Count++;
                lastAuthReset = Date.now();

                if (auth401Count >= MAX_401_RETRIES) {
                    // Too many 401s - stop the loop, show error to user
                    console.error('Multiple 401 errors detected. Stopping re-authentication loop.');
                    store.currentUser = null;
                    Cookies.remove('neatolabsToken');
                    // Don't set authExpired to prevent re-auth loop
                    // Instead redirect to login or show error
                    if (typeof window !== 'undefined') {
                        window.location.href = '/auth-error?reason=token_rejected';
                    }
                } else {
                    store.currentUser = null;
                    Cookies.remove('neatolabsToken');
                    store.status.authExpired = true;
                }
            }
            return Promise.reject(error);
        }
    );
    return instance;
};

export default createAxiosInstance;
